/**
 * Tutorial Sound System
 * Dezente Audio-Feedback für Tutorial-Aktionen
 */

class TutorialSoundSystem {
  private context: AudioContext | null = null;
  private enabled: boolean = true;
  private isSupported: boolean = true;

  constructor() {
    // Lazy initialization - nur wenn gebraucht
    // Check if AudioContext is supported
    this.isSupported = typeof window !== 'undefined' && 
                       (typeof AudioContext !== 'undefined' || typeof (window as any).webkitAudioContext !== 'undefined');
  }

  private getContext(): AudioContext | null {
    if (!this.isSupported) return null;
    
    if (!this.context) {
      try {
        this.context = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (e) {
        console.warn('[Tutorial Sound] AudioContext not supported:', e);
        this.isSupported = false;
        return null;
      }
    }
    return this.context;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled && this.isSupported;
  }

  /**
   * Spielt einen Erfolgs-Sound (Step completed)
   */
  playSuccess() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2); // G5

      gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.4);
    } catch (e) {
      console.warn('[Tutorial Sound] Could not play success sound:', e);
    }
  }

  /**
   * Spielt einen Click-Sound (Element clicked)
   */
  playClick() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.05);
    } catch (e) {
      console.warn('[Tutorial Sound] Could not play click sound:', e);
    }
  }

  /**
   * Spielt einen Start-Sound (Tutorial started)
   */
  playStart() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.setValueAtTime(440, ctx.currentTime); // A4
      oscillator.frequency.setValueAtTime(554.37, ctx.currentTime + 0.1); // C#5

      gainNode.gain.setValueAtTime(0.12, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.warn('[Tutorial Sound] Could not play start sound:', e);
    }
  }

  /**
   * Spielt einen Complete-Sound (Tutorial completed)
   */
  playComplete() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      
      // Triumphant chord progression
      const playNote = (frequency: number, startTime: number, duration: number) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + startTime);
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.08, ctx.currentTime + startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + startTime + duration);

        oscillator.start(ctx.currentTime + startTime);
        oscillator.stop(ctx.currentTime + startTime + duration);
      };

      // C Major chord progression
      playNote(523.25, 0, 0.2);    // C5
      playNote(659.25, 0.1, 0.2);  // E5
      playNote(783.99, 0.2, 0.3);  // G5
      playNote(1046.5, 0.3, 0.4);  // C6
    } catch (e) {
      console.warn('[Tutorial Sound] Could not play complete sound:', e);
    }
  }

  /**
   * Spielt einen Error/Undo-Sound
   */
  playUndo() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.setValueAtTime(300, ctx.currentTime);
      oscillator.frequency.setValueAtTime(200, ctx.currentTime + 0.1);

      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.2);
    } catch (e) {
      console.warn('[Tutorial Sound] Could not play undo sound:', e);
    }
  }
}

// Singleton instance
export const tutorialSound = new TutorialSoundSystem();